import { useState } from "react";
import {
  Alert, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { reportsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * TC-003 gate: posted journals must balance. The TB sums each account's
 * posted debit + credit as of the given date. <code>isBalanced</code> chip
 * is the green/red MVP 1 health indicator.
 */
export function TrialBalancePage() {
  const [asOf, setAsOf] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const query = useQuery({
    queryKey: ["trial-balance", asOf],
    queryFn: () => reportsApi.trialBalance(asOf),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Trial Balance</Typography>
        <TextField label="As of" type="date" size="small"
          InputLabelProps={{ shrink: true }}
          value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </Stack>

      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}

      {query.data && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              As of {new Date(query.data.asOf).toLocaleDateString()} · {query.data.rows.length} accounts with activity
            </Typography>
            <Chip
              color={query.data.isBalanced ? "success" : "error"}
              label={query.data.isBalanced
                ? "Balanced"
                : `Off by ${inr.format(Math.abs(query.data.totalDebit - query.data.totalCredit))}`}
              sx={{ fontWeight: 600 }} />
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Total debit</TableCell>
                <TableCell align="right">Total credit</TableCell>
                <TableCell align="right">Net</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.rows.map(r => (
                <TableRow key={r.accountId} hover>
                  <TableCell><b>{r.code}</b> {r.name}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={r.accountType} />
                  </TableCell>
                  <TableCell align="right">{inr.format(r.totalDebit)}</TableCell>
                  <TableCell align="right">{inr.format(r.totalCredit)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600,
                    color: r.net === 0 ? "text.primary" : r.net > 0 ? "success.main" : "error.main" }}>
                    {r.net === 0 ? "—" : inr.format(r.net)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Totals</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{inr.format(query.data.totalDebit)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{inr.format(query.data.totalCredit)}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableFooter>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
